import { authDataGet, apiGet, apiPost, apiPatch, apiDelete, apiUpload } from './api';
import type {
  DossierOverview, Procurement, DossierContract, ContractDetail,
  Deliverable, ImplementationEvent, ProgressReport, DossierAlert, DossierAuditEntry,
  DossierTask, DossierActivity, ActivityDetail, ActivitiesSummary,
  CorrelatedActivity, CreateActivityPayload
} from '../types';

// Sarcinile din dosare delegate utilizatorului curent (responsible_user_id = eu).
// Backend-ul întoarce { tasks: [...] }; extragem lista.
export async function getMyDossierTasks(): Promise<DossierTask[]> {
  const response = await apiGet<{ tasks: DossierTask[] }>('/auth/data/my-tasks/dossier');
  return response.tasks ?? [];
}

// ============== ACTIVITĂȚI GENERICE PE DOSAR ==============
// Task-uri libere native (tabela dossier.activities), independente de OpenProject.
// Status: pending | in_progress | completed. Responsabil prin UserSelect.
export async function getDossierActivities(dossierId: number): Promise<DossierActivity[]> {
  // Backend întoarce { activities: [...] } — dezambalăm, altfel componenta primește un obiect (crash la .filter).
  const r = await apiGet<{ activities: DossierActivity[] }>(`/auth/data/dossier/${dossierId}/activities`);
  return r.activities ?? [];
}

// Detaliile complete ale unei activități (sursă proiect + documente atașate),
// pentru afișarea în „Activitățile mele" la click pe un element.
// Backend-ul poate întoarce fie { activity: {...} }, fie direct obiectul — fii defensiv.
export async function getActivityDetail(activityId: number): Promise<ActivityDetail> {
  const r = await apiGet<ActivityDetail | { activity: ActivityDetail }>(`/auth/data/activities/${activityId}`);
  const detail = (r && typeof r === 'object' && 'activity' in r)
    ? (r as { activity: ActivityDetail }).activity
    : (r as ActivityDetail);
  // Normalizează lista de documente: garantăm un array chiar dacă backend-ul o omite.
  return { ...detail, documents: detail?.documents ?? [] };
}

export async function createDossierActivity(dossierId: number, data: {
  name: string;
  description?: string;
  status?: DossierActivity['status'];
  responsible_user_id?: number;
  start_date?: string;
  due_date?: string;
  parent_id?: number | null;
  sort_order?: number;
}) {
  return apiPost(`/auth/data/dossier/${dossierId}/activities`, data);
}

// ============== ACTIVITĂȚI CORELATE CU DOCUMENTE ==============
// Butonul „Adaugă activitate" de la fiecare document creează o activitate pe
// proiect (backend-ul rezolvă singur dosarul din projectId) și, dacă primește
// `attachment_id`, o leagă de document în dossier.activity_documents.
// Ruta e pe projectId — la nivel de document avem contextul proiectului,
// nu pe cel al dosarului.
export async function createActivityForProject(
  projectId: number,
  data: CreateActivityPayload
): Promise<DossierActivity> {
  const r = await apiPost<DossierActivity | { activity: DossierActivity }>(
    `/auth/data/projects/${projectId}/activities`,
    data
  );
  // Defensiv: backend-ul poate întoarce fie { activity: {...} }, fie direct obiectul.
  return (r && typeof r === 'object' && 'activity' in r)
    ? (r as { activity: DossierActivity }).activity
    : (r as DossierActivity);
}

// Activitățile deja corelate cu un document, pentru afișarea lor lângă document.
// Întoarce mereu un array: consumatorii fac .map/.length fără gardă.
export async function getActivitiesForDocument(attachmentId: number): Promise<CorrelatedActivity[]> {
  const r = await apiGet<CorrelatedActivity[] | { activities: CorrelatedActivity[] }>(
    `/auth/data/attachments/${attachmentId}/activities`
  );
  if (Array.isArray(r)) return r;
  return r?.activities ?? [];
}

// Leagă un document existent de o activitate existentă (fără a crea o activitate nouă).
export async function linkDocumentToActivity(activityId: number, attachmentId: number) {
  return apiPost(`/auth/data/activities/${activityId}/documents`, { attachment_id: attachmentId });
}

// Desface legătura document ↔ activitate. Șterge doar rândul din
// dossier.activity_documents; nici activitatea, nici fișierul nu sunt afectate.
export async function unlinkDocumentFromActivity(activityId: number, attachmentId: number) {
  return apiDelete(`/auth/data/activities/${activityId}/documents/${attachmentId}`);
}

// Cale imbricată sub dossierId: ruta plată `/dossier/activities/:id` este deja folosită
// de activitățile de achiziție (procurement_activities) și ar prelua altfel aceste cereri.
export async function updateDossierActivity(dossierId: number, id: number, data: {
  name?: string;
  description?: string;
  status?: DossierActivity['status'];
  responsible_user_id?: number | null;
  start_date?: string | null;
  due_date?: string | null;
  parent_id?: number | null;
  sort_order?: number;
}) {
  return apiPatch(`/auth/data/dossier/${dossierId}/activities/${id}`, data);
}

export async function deleteDossierActivity(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/activities/${id}`);
}

// Rezumatul activităților unui dosar, pentru „Progresul dosarului".
// Calculat din dossier.activities (nu din work packages OpenProject, acum goale):
// total / completed / inProgress / todo + procent finalizat.
export async function getActivitiesSummary(dossierId: number): Promise<ActivitiesSummary> {
  const activities = await getDossierActivities(dossierId);
  const total = activities.length;
  const completed = activities.filter((a) => a.status === 'completed').length;
  const inProgress = activities.filter((a) => a.status === 'in_progress').length;
  const todo = activities.filter((a) => a.status === 'pending').length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, inProgress, todo, percent };
}

// Setează statusul la nivel de dosar (dossier_instances.status).
// PATCH /auth/data/dossier/:dossierId/status cu { status }; dezambalează
// răspunsul { status } al backend-ului (fallback pe statusul trimis).
export async function setDossierStatus(dossierId: number, status: string): Promise<string> {
  const r = await apiPatch<{ status?: string }>(`/auth/data/dossier/${dossierId}/status`, { status });
  return r?.status ?? status;
}

// Core dossier
export async function getDossierOverview(projectId: number): Promise<DossierOverview> {
  const response = await authDataGet<DossierOverview>(`/projects/${projectId}/dossier`);
  return response;
}

export async function initializeDossier(projectId: number) {
  return apiPost(`/auth/data/projects/${projectId}/dossier/initialize`, {});
}

// Procurements
export async function getProcurements(dossierId: number): Promise<Procurement[]> {
  return authDataGet<Procurement[]>(`/dossier/${dossierId}/procurements`);
}

export async function createProcurement(dossierId: number, data: { name: string; procurement_type?: string; estimated_value?: number; responsible_user_id?: number; notes?: string; document_type_ids?: number[]; start_date?: string; end_date?: string }) {
  return apiPost(`/auth/data/dossier/${dossierId}/procurements`, data);
}

export async function getProcurement(dossierId: number, id: number) {
  return authDataGet(`/dossier/${dossierId}/procurements/${id}`);
}

export async function updateProcurement(dossierId: number, id: number, data: any) {
  return apiPatch(`/auth/data/dossier/${dossierId}/procurements/${id}`, data);
}

export async function deleteProcurement(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/procurements/${id}`);
}

export async function updateActivity(activityId: number, data: { status?: string; notes?: string }) {
  return apiPatch(`/auth/data/dossier/activities/${activityId}`, data);
}

export async function uploadActivityDocument(activityId: number, file: File, documentType?: string) {
  return apiUpload(`/auth/data/dossier/activities/${activityId}/documents`, file, { document_type: documentType });
}

export async function addProcurementDocument(procId: number, file: File, metadata: { document_type_id: number; title?: string; notes?: string; start_date?: string; end_date?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiUpload(`/auth/data/dossier/procurements/${procId}/add-document`, file, metadata);
}

export async function updateProcurementDocument(docId: number, data: { document_type_id?: number; title?: string; notes?: string; start_date?: string; end_date?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiPatch(`/auth/data/dossier/documents/${docId}`, data);
}

export async function deleteDocument(docId: number) {
  return apiDelete(`/auth/data/dossier/documents/${docId}`);
}

// Contracts
export async function getContracts(dossierId: number): Promise<DossierContract[]> {
  return authDataGet<DossierContract[]>(`/dossier/${dossierId}/contracts`);
}

export async function createContract(dossierId: number, data: any) {
  return apiPost(`/auth/data/dossier/${dossierId}/contracts`, data);
}

export async function getContractDetail(dossierId: number, id: number): Promise<ContractDetail> {
  return authDataGet<ContractDetail>(`/dossier/${dossierId}/contracts/${id}`);
}

export async function updateContract(dossierId: number, id: number, data: any) {
  return apiPatch(`/auth/data/dossier/${dossierId}/contracts/${id}`, data);
}

export async function deleteContract(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/contracts/${id}`);
}

// Addendums (acte adiționale)
// amount_mode determină interpretarea lui value_change:
//  - 'delta'    → diferență (+/-) adunată la baza curentă
//  - 'absolute' → valoare nouă totală care înlocuiește baza
//  - 'none'     → nu schimbă valoarea (doar durată/clauze); value_change ignorat
export type AddendumAmountMode = 'delta' | 'absolute' | 'none';

export interface AddendumInput {
  addendum_number?: string;
  addendum_type?: string;
  addendum_date?: string;
  amount_mode?: AddendumAmountMode;
  value_change?: number;
  new_end_date?: string;
  description?: string;
}

export async function createAddendum(contractId: number, data: AddendumInput) {
  return apiPost(`/auth/data/dossier/contracts/${contractId}/addendums`, data);
}

export async function updateAddendum(contractId: number, id: number, data: AddendumInput) {
  return apiPatch(`/auth/data/dossier/contracts/${contractId}/addendums/${id}`, data);
}

export async function deleteAddendum(contractId: number, id: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/addendums/${id}`);
}

export async function uploadAddendumFile(contractId: number, id: number, file: File) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/addendums/${id}/upload`, file);
}

// Guarantees
export async function createGuarantee(contractId: number, data: any) {
  return apiPost(`/auth/data/dossier/contracts/${contractId}/guarantees`, data);
}

export async function updateGuarantee(contractId: number, id: number, data: { guarantee_type?: string; amount?: number; issue_date?: string; expiry_date?: string; issuer?: string }) {
  return apiPatch(`/auth/data/dossier/contracts/${contractId}/guarantees/${id}`, data);
}

export async function deleteGuarantee(contractId: number, id: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/guarantees/${id}`);
}

export async function uploadGuaranteeFile(contractId: number, id: number, file: File) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/guarantees/${id}/upload`, file);
}

// Receptions
export async function createReception(contractId: number, data: any) {
  return apiPost(`/auth/data/dossier/contracts/${contractId}/receptions`, data);
}

export async function updateReception(contractId: number, id: number, data: { reception_type?: string; reception_date?: string; reception_number?: string; description?: string }) {
  return apiPatch(`/auth/data/dossier/contracts/${contractId}/receptions/${id}`, data);
}

export async function deleteReception(contractId: number, id: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/receptions/${id}`);
}

export async function uploadReceptionFile(contractId: number, id: number, file: File) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/receptions/${id}/upload`, file);
}

// Invoices
export async function getInvoices(contractId: number) {
  return authDataGet(`/dossier/contracts/${contractId}/invoices`);
}

export async function createInvoice(contractId: number, data: any) {
  return apiPost(`/auth/data/dossier/contracts/${contractId}/invoices`, data);
}

export async function updateInvoice(contractId: number, id: number, data: { invoice_number?: string; invoice_date?: string; amount?: number; vat_amount?: number; total_amount?: number }) {
  return apiPatch(`/auth/data/dossier/contracts/${contractId}/invoices/${id}`, data);
}

export async function deleteInvoice(contractId: number, id: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/invoices/${id}`);
}

export async function uploadInvoiceFile(contractId: number, id: number, file: File) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/invoices/${id}/upload`, file);
}

// Contract Documents
export async function getContractDocuments(contractId: number) {
  return authDataGet(`/dossier/contracts/${contractId}/documents`);
}

// Documentele de contract sunt pur descriptive (fișiere + metadate): NU au efect financiar,
// de aceea câmpul `value` nu mai este expus aici.
export async function createContractDocument(contractId: number, data: { document_name?: string; notes?: string; start_date?: string; end_date?: string; document_type?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiPost(`/auth/data/dossier/contracts/${contractId}/documents/create`, data);
}

// Notă: documentele de contract NU au nomenclator de tipuri (nu există `contract_document_types`),
// iar backend-ul citește `document_type` ca text liber. De aceea NU expunem `document_type_id` aici:
// ar fi trimis, ignorat de server și ar da impresia falsă că tipul se poate schimba.
export async function updateContractDocument(contractId: number, docId: number, data: { document_name?: string; notes?: string; start_date?: string; end_date?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string; clear_file?: boolean }) {
  return apiPatch(`/auth/data/dossier/contracts/${contractId}/documents/${docId}`, data);
}

export async function uploadContractDocument(contractId: number, file: File, documentType?: string) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/documents`, file, { document_type: documentType });
}

export async function uploadFileToDocument(contractId: number, docId: number, file: File) {
  return apiUpload(`/auth/data/dossier/contracts/${contractId}/documents/${docId}/upload`, file);
}

export async function deleteContractDocument(contractId: number, docId: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/documents/${docId}`);
}

export async function deleteContractFile(contractId: number, fileId: number) {
  return apiDelete(`/auth/data/dossier/contracts/${contractId}/files/${fileId}`);
}

// Deliverables
export async function getDeliverables(dossierId: number): Promise<Deliverable[]> {
  return authDataGet<Deliverable[]>(`/dossier/${dossierId}/deliverables`);
}

export async function createDeliverable(dossierId: number, data: any) {
  return apiPost(`/auth/data/dossier/${dossierId}/deliverables`, data);
}

export async function updateDeliverable(dossierId: number, id: number, data: any) {
  return apiPatch(`/auth/data/dossier/${dossierId}/deliverables/${id}`, data);
}

export async function deleteDeliverable(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/deliverables/${id}`);
}

// Deliverable Documents
export async function uploadDeliverableDocument(deliverableId: number, file: File, documentType?: string) {
  return apiUpload(`/auth/data/dossier/deliverables/${deliverableId}/documents`, file, { document_type: documentType });
}

export async function addDeliverableDocument(deliverableId: number, file: File, metadata: { title?: string; notes?: string; document_type_id?: number; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiUpload(`/auth/data/dossier/deliverables/${deliverableId}/add-document`, file, metadata);
}

export async function updateDeliverableDocument(deliverableId: number, docId: number, data: { title?: string; notes?: string; document_type_id?: number; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiPatch(`/auth/data/dossier/deliverables/${deliverableId}/documents/${docId}`, data);
}

export async function deleteDeliverableDocument(deliverableId: number, docId: number) {
  return apiDelete(`/auth/data/dossier/deliverables/${deliverableId}/documents/${docId}`);
}

// Implementation Events
export async function getEvents(dossierId: number): Promise<ImplementationEvent[]> {
  return authDataGet<ImplementationEvent[]>(`/dossier/${dossierId}/events`);
}

export async function createEvent(dossierId: number, data: any) {
  return apiPost(`/auth/data/dossier/${dossierId}/events`, data);
}

export async function updateEvent(dossierId: number, id: number, data: any) {
  return apiPatch(`/auth/data/dossier/${dossierId}/events/${id}`, data);
}

export async function deleteEvent(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/events/${id}`);
}

// Event Documents
export async function uploadEventDocument(eventId: number, file: File, documentType?: string) {
  return apiUpload(`/auth/data/dossier/events/${eventId}/documents`, file, { document_type: documentType });
}

export async function addEventDocument(eventId: number, file: File, metadata: { document_type_id?: number; title?: string; notes?: string; start_date?: string; end_date?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiUpload(`/auth/data/dossier/events/${eventId}/add-document`, file, metadata);
}

export async function updateEventDocument(eventId: number, docId: number, data: { title?: string; notes?: string; document_type_id?: number; start_date?: string; end_date?: string; nr_registratura?: string; data_registratura?: string; data_document?: string; data_expirare?: string }) {
  return apiPatch(`/auth/data/dossier/events/${eventId}/documents/${docId}`, data);
}

export async function deleteEventDocument(eventId: number, docId: number) {
  return apiDelete(`/auth/data/dossier/events/${eventId}/documents/${docId}`);
}

// Progress
export async function getProgress(dossierId: number): Promise<ProgressReport[]> {
  return authDataGet<ProgressReport[]>(`/dossier/${dossierId}/progress`);
}

export async function createProgress(dossierId: number, data: any) {
  return apiPost(`/auth/data/dossier/${dossierId}/progress`, data);
}

export async function deleteProgress(dossierId: number, id: number) {
  return apiDelete(`/auth/data/dossier/${dossierId}/progress/${id}`);
}

// Alerts
export async function getAlerts(dossierId: number): Promise<DossierAlert[]> {
  return authDataGet<DossierAlert[]>(`/dossier/${dossierId}/alerts`);
}

export async function dismissAlert(dossierId: number, alertId: number) {
  return apiPatch(`/auth/data/dossier/${dossierId}/alerts/${alertId}/dismiss`, {});
}

// Audit Log
export async function getAuditLog(dossierId: number, limit = 50, offset = 0): Promise<DossierAuditEntry[]> {
  return authDataGet<DossierAuditEntry[]>(`/dossier/${dossierId}/audit-log?limit=${limit}&offset=${offset}`);
}
